import moment from "moment";
import { MentorAvailability, MentorAbsenceType, CellSessionData, CellRenderState, CellActionMode } from "../Types";

/** Keys on MentorAvailability that are not day/time slot booleans. */
export const KEYS_TO_SKIP: string[] = [
    "mentorId",
    "firstName",
    "lastName",
    "mentorAvailabilityId",
    "mentorStudentRota",
    "mentorRotaAbsences",
    "mentorRotaAbsenses", // typo in API response — must match actual key name
    "monAMOverride",
    "monPMOverride",
    "tuesAMOverride",
    "tuesPMOverride",
    "wedsAMOverride",
    "wedsPMOverride",
    "thurAMOverride",
    "thurPMOverride",
    "friAMOverride",
    "friPMOverride",
];

export const MENTOR_AVAILABILITY_COLUMNS = [
    {
        day: "Monday",
        time: [
            { name: "am", value: "monAM" },
            { name: "pm", value: "monPM" },
        ],
    },
    {
        day: "Tuesday",
        time: [
            { name: "am", value: "tuesAM" },
            { name: "pm", value: "tuesPM" },
        ],
    },
    {
        day: "Wednesday",
        time: [
            { name: "am", value: "wedsAM" },
            { name: "pm", value: "wedsPM" },
        ],
    },
    {
        day: "Thursday",
        time: [
            { name: "am", value: "thurAM" },
            { name: "pm", value: "thurPM" },
        ],
    },
    {
        day: "Friday",
        time: [
            { name: "am", value: "friAM" },
            { name: "pm", value: "friPM" },
        ],
    },
];

export class DomainManager {
    // No viewModel reference needed — all methods are pure transformations.

    /** Returns a background colour string based on a session status name. */
    public renderColour(status: string): string {
        if (status === "Pending") return "#f9f9f9";
        if (status === "OverDue") return "#fce4bf";
        if (status === "Submit") return "#c3e9e5";
        if (status === "notAvailable") return "#fffbe8";
        return "#fff";
    }

    /** Returns the text colour for a session status name in live mode. */
    public statusTextColour(sessionStatusName: string): string {
        if (sessionStatusName === "Submitted") return "#11A696";
        if (sessionStatusName === "Pending") return "#C15DB0";
        if (sessionStatusName === "Overdue") return "#F39200";
        return "";
    }

    /** Returns the display text for attendance state (null → "", true → "Attended", false → "Absent"). */
    public attendanceText(didAttend: boolean | null): string {
        if (didAttend === null || didAttend === undefined) return "";
        return didAttend ? "Attended" : "Absent";
    }

    /** True when the session status is "Overdue" (transport not required). */
    public isOverdueSession(sessionStatusName: string): boolean {
        return sessionStatusName === "Overdue";
    }

    /** Label for the session report button based on attendance. */
    public sessionReportButtonLabel(didAttend: boolean | null): string {
        return didAttend === false ? "VIEW REASON WHY" : "VIEW SESSION REPORT";
    }

    /** Returns the cell background colour in live mode based on session status. */
    public liveModeBackgroundColour(sessionStatusName: string): string {
        if (sessionStatusName === "Submitted") return "#C3E9E5";
        if (sessionStatusName === "Pending") return "#F0D6EB";
        if (sessionStatusName === "Overdue") return "#FCE4BF";
        return "";
    }

    /** Checks whether a date falls within an inclusive date range. */
    public dateInRange(date: Date, startDate: Date, endDate: Date): boolean {
        return date >= startDate && date <= endDate;
    }

    /**
     * Returns resolved session data for a mentor availability slot.
     * Returns null when the mentor has no student assigned for that slot.
     */
    public findStudentSession(mentor: MentorAvailability, availabilityKey: string): CellSessionData | null {
        if (!mentor.mentorStudentRota?.length) return null;

        const matchingRota = mentor.mentorStudentRota.find((rota) => {
            for (const key in rota) {
                if (key.includes(availabilityKey) && (rota as any)[key] !== null) {
                    return true;
                }
            }
            return false;
        });

        if (!matchingRota) return null;

        return {
            studentName: `${matchingRota.firstName} ${matchingRota.lastName}`,
            studentId: matchingRota.id,
            isStudentSessionAvailable: true,
            isMentorAbsenceOverride: true,
            mentorStudentRotaId: matchingRota.mentorStudentRotaId,
            studentSessionId: matchingRota.studentSessionId,
            sessionStartTime: matchingRota.sessionStartTime,
            sessionEndTime: matchingRota.sessionEndTime,
            reportIncident: matchingRota.reportIncident,
            didAttend: matchingRota.didAttend,
            sessionStatusId: matchingRota.sessionStatusId,
            sessionStatusName: matchingRota.sessionStatusName,
        };
    }

    /** Maps an availability key (e.g. "monAM") to the timetable property names on a student record. */
    public getDayKeysFromAvailability(availabilityKey: string): { startProp: string; endProp: string } {
        const lower = availabilityKey.toLowerCase();
        if (lower.startsWith("mon")) return { startProp: "startTimeMon", endProp: "endTimeMon" };
        if (lower.startsWith("tues")) return { startProp: "startTimeTues", endProp: "endTimeTues" };
        if (lower.startsWith("weds")) return { startProp: "startTimeWeds", endProp: "endTimeWeds" };
        if (lower.startsWith("thur")) return { startProp: "startTimeThur", endProp: "endTimeThur" };
        if (lower.startsWith("fri")) return { startProp: "startTimeFri", endProp: "endTimeFri" };
        return { startProp: "", endProp: "" };
    }

    /** Normalises a value to a lowercase, whitespace-stripped string for comparison. */
    public normalise(s: any): string {
        return (s || "").toString().replace(/\s+/g, "").toLowerCase();
    }

    /** Formats a time value (HH:mm:ss, HH:mm, or ISO) to "h:mm a" for display. Returns "" for invalid values. */
    public formatTimeForDisplay(value: any): string {
        if (!value) return "";
        const m = moment(value, ["HH:mm:ss", "HH:mm", moment.ISO_8601], true);
        return m.isValid() ? m.format("h:mm a") : "";
    }

    /**
     * Returns the preferred start/end times for a cell.
     * Prefers timetable times from studentList; falls back to session times.
     */
    public getPreferredTimesForCell(mentor: MentorAvailability, availabilityKey: string, studentList: any[]): { start: any; end: any } {
        const session = this.findStudentSession(mentor, availabilityKey);
        if (!session) return { start: null, end: null };

        const { startProp, endProp } = this.getDayKeysFromAvailability(availabilityKey);

        let student = session.studentId ? studentList.find((s) => this.normalise(s.studentId) === this.normalise(session.studentId)) : null;

        if (!student) {
            student = studentList.find((s) => this.normalise(s.studentName || `${s.firstName || ""}${s.lastName || ""}`) === this.normalise(session.studentName));
        }

        return {
            start: (student ? student[startProp] : null) ?? session.sessionStartTime ?? null,
            end: (student ? student[endProp] : null) ?? session.sessionEndTime ?? null,
        };
    }

    /**
     * Finds the MentorAbsenceType that covers a given slot key on the week starting at `weekStartDate`.
     * Returns the absence record (or undefined) — does NOT trigger side effects.
     */
    public findAbsenceForSlot = (key: string, weekStartDate: any, mentor: MentorAvailability): MentorAbsenceType | undefined => {
        const slotDate = moment(weekStartDate);

        switch (true) {
            case key.startsWith("mon"):
                slotDate.day("Monday");
                break;
            case key.startsWith("tues"):
                slotDate.day("Tuesday");
                break;
            case key.startsWith("weds"):
                slotDate.day("Wednesday");
                break;
            case key.startsWith("thur"):
                slotDate.day("Thursday");
                break;
            case key.startsWith("fri"):
                slotDate.day("Friday");
                break;
            default:
                return undefined;
        }

        if (key.includes("PM")) {
            slotDate.add(12, "hours");
        }

        const target = slotDate.toDate();

        const absences: MentorAbsenceType[] = mentor.mentorRotaAbsences ?? (mentor as any).mentorRotaAbsenses ?? [];
        return absences.find((item) => {
            const startRaw = item.absenceStartDate ?? (item as any).absenseStartDate;
            const endRaw = item.absenceEndDate ?? (item as any).absenseEndDate;
            const start = moment(startRaw).startOf("day").toDate();
            const end = moment(endRaw).endOf("day").toDate();
            return this.dateInRange(target, start, end);
        });
    };

    /**
     * Computes the CSS grid-template-columns string for a mentor row.
     * Slots with a full-day session (spanning AM+PM) get "2fr" each; others get "1fr 1fr".
     */
    public selectGridTemplateColumns(mentor: MentorAvailability): string {
        if (!mentor?.mentorStudentRota?.length) {
            return "1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr";
        }

        const spans = this.computeDaySpans(mentor);
        const days = ["mon", "tues", "weds", "thur", "fri"];
        return days.map((day) => (spans[day] === "2fr" ? "2fr" : "1fr 1fr")).join(" ");
    }

    /**
     * Returns per-slot grid fraction values for nullification checks.
     * PM slots that are part of a spanning session return "2fr" and should be hidden.
     */
    public selectGridTemplateColumnsForNullification(mentor: MentorAvailability): Record<string, string> {
        const defaults: Record<string, string> = {
            monAM: "1fr",
            monPM: "1fr",
            tuesAM: "1fr",
            tuesPM: "1fr",
            wedsAM: "1fr",
            wedsPM: "1fr",
            thurAM: "1fr",
            thurPM: "1fr",
            friAM: "1fr",
            friPM: "1fr",
        };

        if (!mentor?.mentorStudentRota?.length) return defaults;

        const result = { ...defaults };
        const dayMap: Record<string, string> = {
            mon: "monAMStudentSessionId",
            tues: "tuesAMStudentSessionId",
            weds: "wedsAMStudentSessionId",
            thur: "thurAMStudentSessionId",
            fri: "friAMStudentSessionId",
        };

        for (const rota of mentor.mentorStudentRota) {
            if (!rota?.sessionStartTime || !rota?.sessionEndTime) continue;
            const start = moment(rota.sessionStartTime, "HH:mm:ss");
            const end = moment(rota.sessionEndTime, "HH:mm:ss");
            const spansNoon = (start.hours() < 12 || (start.hours() === 12 && start.minutes() === 0)) && (end.hours() > 12 || (end.hours() === 12 && end.minutes() > 0));

            if (!spansNoon) continue;

            for (const [day, sessionKey] of Object.entries(dayMap)) {
                if ((rota as any)[sessionKey]) {
                    result[`${day}AM`] = "2fr";
                    result[`${day}PM`] = "2fr";
                }
            }
        }

        return result;
    }

    /**
     * Returns the ordered list of slot keys for a mentor row, excluding non-slot keys
     * and PM slots that are hidden because an AM+PM spanning session occupies both cells.
     */
    public getVisibleSlotKeys(mentor: MentorAvailability): string[] {
        const nullificationMap = this.selectGridTemplateColumnsForNullification(mentor);
        const pmKeys = new Set(["monPM", "tuesPM", "wedsPM", "thurPM", "friPM"]);
        return Object.keys(mentor).filter((key) => {
            if (KEYS_TO_SKIP.includes(key)) return false;
            if (pmKeys.has(key) && nullificationMap[key] === "2fr") return false;
            return true;
        });
    }

    /** Maps a session date to the timetable property names (startTimeMon, etc.) based on day of week. */
    public getDayKeyPropsFromDate(sessionDate: string): { startProp: string; endProp: string } {
        const day = moment(sessionDate).format("dddd");
        switch (day) {
            case "Monday":
                return { startProp: "startTimeMon", endProp: "endTimeMon" };
            case "Tuesday":
                return { startProp: "startTimeTues", endProp: "endTimeTues" };
            case "Wednesday":
                return { startProp: "startTimeWeds", endProp: "endTimeWeds" };
            case "Thursday":
                return { startProp: "startTimeThur", endProp: "endTimeThur" };
            case "Friday":
                return { startProp: "startTimeFri", endProp: "endTimeFri" };
            default:
                return { startProp: "", endProp: "" };
        }
    }

    /** Formats a time value (HH:mm:ss, HH:mm, or ISO) to "HH:mm". Returns " " for invalid values. */
    public formatTimeHHmm(value: any): string {
        if (!value) return " ";
        const m = moment(value, ["HH:mm:ss", "HH:mm", moment.ISO_8601], true);
        return m.isValid() ? m.format("HH:mm") : " ";
    }

    /**
     * Resolves display start/end times for the session detail modal.
     * Prefers timetable times from studentList; falls back to raw session times.
     */
    public getStudentSessionTime(
        sessionDate: string,
        studentId: string,
        studentName: string,
        studentList: any[],
        fallbackStart: string,
        fallbackEnd: string,
    ): { start: string; end: string } {
        const { startProp, endProp } = this.getDayKeyPropsFromDate(sessionDate);
        const student =
            studentList.find((s) => this.normalise(s.studentId) === this.normalise(studentId)) ||
            studentList.find((s) => this.normalise(s.studentName || `${s.firstName || ""}${s.lastName || ""}`) === this.normalise(studentName));
        return {
            start: this.formatTimeHHmm((student ? (student as any)[startProp] : null) ?? fallbackStart),
            end: this.formatTimeHHmm((student ? (student as any)[endProp] : null) ?? fallbackEnd),
        };
    }

    /**
     * Computes all display state for a single schedule cell.
     * Centralises every condition that would otherwise live in JSX.
     */
    public getCellState(mentor: MentorAvailability, slotKey: string, studentList: any[], rotaEditable: boolean): CellRenderState {
        const isAvailable = (mentor as any)[slotKey] as boolean;
        const isOverride = (mentor as any)[`${slotKey}Override`] as boolean;
        const session = this.findStudentSession(mentor, slotKey);
        const isSessionAvailable = session?.isStudentSessionAvailable ?? false;

        const liveModeBackground = !rotaEditable && session ? this.liveModeBackgroundColour(session.sessionStatusName) : "";
        const cellBackground = isAvailable ? this.renderColour("Pending") : this.renderColour("notAvailable");

        const times = !rotaEditable && isSessionAvailable ? this.getPreferredTimesForCell(mentor, slotKey, studentList) : { start: null, end: null };

        const displayStart = this.formatTimeForDisplay(times.start);
        const displayEnd = this.formatTimeForDisplay(times.end);

        const didAttend = session?.didAttend ?? null;
        const hasAttendanceRecord = didAttend === true || didAttend === false;

        const showCrossIcon = isOverride && !isAvailable;
        const showOverTimeButton = !isAvailable && rotaEditable && !isSessionAvailable;
        const showEditUnavailableDelete = !isAvailable && rotaEditable && isSessionAvailable;
        const actionMode: CellActionMode = showCrossIcon
            ? CellActionMode.CrossIcon
            : showOverTimeButton
            ? CellActionMode.OverTime
            : showEditUnavailableDelete
            ? CellActionMode.UnavailableDelete
            : CellActionMode.Session;
        const showTimes = !rotaEditable && isSessionAvailable && (!!displayStart || !!displayEnd);

        return {
            background: liveModeBackground || cellBackground,
            actionMode,
            studentName: session?.studentName ?? "",
            sessionStatusText: session?.sessionStatusName?.toUpperCase() ?? "",
            studentSessionId: session?.studentSessionId ?? null,
            mentorStudentRotaId: session?.mentorStudentRotaId ?? null,
            displayStart,
            displayEnd,
            formattedTimeRange: showTimes ? `(${displayStart} - ${displayEnd})` : "",
            sessionStatusColour: session ? this.statusTextColour(session.sessionStatusName) : "",
            attendanceColour: didAttend ? "#27E6A3" : "#E6054E",
            attendanceText: didAttend === null ? "" : didAttend === true ? "Attended" : "Absent",
            incidentText: session?.reportIncident ? "(INCIDENT REPORTED)" : "",
            showEditUnavailableAssigned: !isAvailable && rotaEditable && isSessionAvailable,
            showRemoveDetailsButton: !rotaEditable && isSessionAvailable && hasAttendanceRecord,
            showCrossIconAction: actionMode === CellActionMode.CrossIcon,
            showOverTimeAction: actionMode === CellActionMode.OverTime,
            showUnavailableDeleteAction: actionMode === CellActionMode.UnavailableDelete,
            showSessionContent: actionMode === CellActionMode.Session,
            showLiveModeStatus: !rotaEditable && isSessionAvailable,
            showStudentNameLink: isSessionAvailable,
            showTimes,
            showAddStudentButton: rotaEditable && !isSessionAvailable,
            showEditDeleteButton: rotaEditable && isSessionAvailable,
        };
    }

    // ─── Private Helpers ──────────────────────────────────────────────────────

    private computeDaySpans(mentor: MentorAvailability): Record<string, string> {
        const spans: Record<string, string> = { mon: "1fr", tues: "1fr", weds: "1fr", thur: "1fr", fri: "1fr" };
        const dayMap: Record<string, string> = {
            mon: "monAMStudentSessionId",
            tues: "tuesAMStudentSessionId",
            weds: "wedsAMStudentSessionId",
            thur: "thurAMStudentSessionId",
            fri: "friAMStudentSessionId",
        };

        for (const rota of mentor.mentorStudentRota) {
            if (!rota?.sessionStartTime || !rota?.sessionEndTime) continue;
            const start = moment(rota.sessionStartTime, "HH:mm:ss");
            const end = moment(rota.sessionEndTime, "HH:mm:ss");
            const spansNoon = (start.hours() < 12 || (start.hours() === 12 && start.minutes() === 0)) && (end.hours() > 12 || (end.hours() === 12 && end.minutes() > 0));

            if (!spansNoon) continue;

            for (const [day, sessionKey] of Object.entries(dayMap)) {
                if ((rota as any)[sessionKey]) spans[day] = "2fr";
            }
        }

        return spans;
    }
}
